import React, { useState, useEffect, useRef } from 'react';
import { trackStepCompletion, trackConfigurationChange, startStepTimer, trackStepTiming } from '../services/analytics';

// Convert color name to URL-friendly format (handleize)
function handleize(str) {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

// Generate image URL from color name
function getColorImageUrl(colorName) {
  const handleizedName = handleize(colorName);
  return `https://ervp-spa.s3.us-east-1.amazonaws.com/shade-builder/static/images/${handleizedName}.jpg`;
}

function ColorSelector({ selectedType, colors, shadeConfiguration, onUpdate }) {
  const [colorOptions, setColorOptions] = useState(null);
  const [samplePackLink, setSamplePackLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasTrackedCompletion, setHasTrackedCompletion] = useState(false);
  const prevCompleteRef = useRef(null);

  useEffect(() => {
    // Reset completion tracking when shade type changes
    prevCompleteRef.current = null;
    setHasTrackedCompletion(false);

    async function loadColorOptions() {
      try {
        const response = await fetch('https://ervp-spa.s3.us-east-1.amazonaws.com/shade-builder/products.json');
        if (!response.ok) {
          throw new Error('Failed to load color options');
        }
        const data = await response.json();
        if (data.colorOptions && data.colorOptions[selectedType]) {
          setColorOptions(data.colorOptions[selectedType]);
        }
        if (data.samplePackLinks && data.samplePackLinks[selectedType]) {
          setSamplePackLink(data.samplePackLinks[selectedType]);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error loading color options:', error);
        setLoading(false);
      }
    }

    loadColorOptions();
  }, [selectedType]);

  const handleColorSelect = (component, colorName) => {
    // Track color change
    if (colors[component] !== colorName) {
      trackConfigurationChange(`color_${component}`, colors[component], colorName, 3);
    }
    
    onUpdate({
      colors: {
        ...colors,
        [component]: colorName
      }
    });
  };

  // Track step 3 completion when all required colors are selected
  // Dynamically check based on what options exist in colorOptions
  useEffect(() => {
    if (!colorOptions) return;

    // Only require topRail if it exists in colorOptions
    const topRailRequired = colorOptions.topRail && colorOptions.topRail.length > 0;
    const topRailComplete = !topRailRequired || colors.topRail;

    const nightShadeComplete = colors.nightShade;
    const dayShadeComplete = shadeConfiguration === 'night' || colors.dayShade;

    const isComplete = topRailComplete && nightShadeComplete && dayShadeComplete;

    // Only notify parent if completion status changed (prevents infinite loops)
    if (prevCompleteRef.current !== isComplete) {
      prevCompleteRef.current = isComplete;
      onUpdate({ step3Complete: isComplete });
    }

    if (isComplete && !hasTrackedCompletion) {
      trackStepTiming(3);
      trackStepCompletion(3, {
        shade_type: selectedType,
        configuration: shadeConfiguration,
        top_rail_color: colors.topRail || 'none',
        night_shade_color: colors.nightShade,
        day_shade_color: colors.dayShade || 'none',
        has_day_shade: shadeConfiguration === 'day-night'
      });
      startStepTimer(4);
      setHasTrackedCompletion(true);
    }
  }, [colors, colorOptions, shadeConfiguration, selectedType, hasTrackedCompletion]);

  const renderColorOptions = (component, label, required = true) => {
    if (!colorOptions || !colorOptions[component]) {
      return null;
    }

    const options = colorOptions[component];
    const selectedColor = colors[component];

    return (
      <div className="tw-mb-6">
        <h3 className="tw-text-lg tw-font-medium tw-mb-3">
          {label} {required && <span className="tw-text-red-500">*</span>}
        </h3>
        <div className="tw-grid tw-grid-cols-3 sm:tw-grid-cols-4 md:tw-grid-cols-6 tw-gap-2">
          {options.map((colorName) => (
            <button
              key={colorName}
              onClick={() => handleColorSelect(component, colorName)}
              className={`tw-relative tw-p-2 tw-rounded-lg tw-border-2 tw-transition-all
                ${selectedColor === colorName
                  ? 'tw-border-brand-red tw-shadow-md'
                  : 'tw-border-gray-300 hover:tw-border-gray-400'}`}
            >
              <img 
                src={getColorImageUrl(colorName)} 
                alt={colorName}
                className="tw-w-full tw-aspect-square tw-rounded tw-mb-1 tw-border tw-border-gray-200 tw-object-cover"
                loading="lazy"
              />
              <p className="tw-text-xs tw-font-medium">{colorName}</p>
              {selectedColor === colorName && (
                <div className="tw-absolute tw-top-1 tw-right-1 tw-bg-brand-red tw-text-white tw-rounded-full tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center">
                  <svg className="tw-w-2.5 tw-h-2.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-py-8">
        <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-brand-red"></div>
      </div>
    );
  }

  if (!colorOptions) {
    return (
      <div className="tw-text-center tw-py-8 tw-text-gray-500">
        Color options not available for this shade type.
      </div>
    );
  }

  return (
    <div>
      {/* Sample Pack Link */}
      {samplePackLink && (
        <div className="tw-mb-4">
          <a 
            href={samplePackLink} 
            target="_blank" 
            rel="noopener noreferrer"
            className="tw-text-brand-red hover:tw-underline tw-font-medium"
          >
            Want to buy a Color Sample pack first?
          </a>
        </div>
      )}

      {/* Pull Rail */}
      {renderColorOptions('topRail', 'Pull Rail Color')}

      {/* Night Shade */}
      {renderColorOptions('nightShade', 'Night Shade Color')}

      {/* Day Shade (only if Day & Night configuration) */}
      {shadeConfiguration === 'day-night' && (
        renderColorOptions('dayShade', 'Day Shade Color')
      )}

    </div>
  );
}

export default ColorSelector;